/**
 * Risk & quality as three pictures: the condition report, the environmental
 * report and the FEMA flood zone, each read at a glance.
 *
 * The panel used to set each one out as a sentence ("No property condition
 * report uploaded", a FEMA refusal reason in full), and three sentences did not
 * fit the slot (Edward, 29 Oct 2026: "an infographic representation of each").
 * Each tile now shows a mark and one or two words; the full sentence stays for
 * a screen reader and on hover.
 *
 * Nothing here grades a building. A report is on file or it is not; its
 * findings are not read into the record. The flood tile places a zone on
 * FEMA's own published scale, and shows no scale at all for a zone nobody
 * recorded.
 */
import type { ReactNode } from 'react'
import { Check, ClipboardCheck, Leaf, Minus, Waves, type LucideIcon } from 'lucide-react'

export type FloodRisk = 'minimal' | 'moderate' | 'high' | 'undetermined'

export type FloodReading = {
  /** The zone as FEMA writes it: "AE", "X", "VE"... */
  zone: string
  risk: FloodRisk
}

// The zone leads the recorded text ("AE — 1 PCT ANNUAL CHANCE…", "Zone X",
// "X (shaded)"). Longer codes first, so "AE" is not read as "A".
const ZONE_RE = /^\s*(?:fema\s+)?(?:flood\s+)?(?:zone\s+)?(A99|AE|AH|AO|AR|VE|V|A|X500|X|B|C|D)(?![a-z0-9])/i
const MODERATE_RE = /0\.2\s*(?:%|pct|percent)|shaded|500[-\s]?year/i
const MINIMAL_RE = /unshaded|minimal|outside the 0\.2/i

/**
 * Where a recorded flood zone sits on FEMA's scale, or null when the text names
 * no zone (nothing recorded, or a lookup's reason for not answering).
 *
 * FEMA's definitions: A and V zones are the Special Flood Hazard Area (1%
 * annual chance, flood insurance required on a federally backed loan); B and
 * shaded X are the 0.2% annual-chance area (moderate); C and unshaded X are
 * minimal; D is undetermined. A bare "X" that does not say which is read as
 * minimal, the unshaded X being by far the more common.
 */
export function readFloodZone(text: string | null | undefined): FloodReading | null {
  const match = ZONE_RE.exec(text ?? '')
  if (!match) return null
  const zone = match[1].toUpperCase()
  const rest = (text ?? '').slice(match[0].length)
  if (zone.startsWith('A') || zone.startsWith('V')) return { zone, risk: 'high' }
  if (zone === 'D') return { zone, risk: 'undetermined' }
  if (zone === 'B' || zone === 'X500') return { zone, risk: 'moderate' }
  if (zone === 'C') return { zone, risk: 'minimal' }
  // X: the detail says which X it is, when it says anything.
  if (MINIMAL_RE.test(rest)) return { zone, risk: 'minimal' }
  if (MODERATE_RE.test(rest)) return { zone, risk: 'moderate' }
  return { zone, risk: 'minimal' }
}

const SCALE: { risk: Exclude<FloodRisk, 'undetermined'>; label: string; lit: string }[] = [
  { risk: 'minimal', label: 'Min', lit: 'bg-emerald-400' },
  { risk: 'moderate', label: 'Mod', lit: 'bg-amber-400' },
  { risk: 'high', label: 'High', lit: 'bg-red-400' },
]

const RISK_CAPTION: Record<FloodRisk, string> = {
  minimal: 'Minimal risk',
  moderate: 'Moderate risk',
  high: 'High risk · SFHA',
  undetermined: 'Undetermined',
}

const RISK_TONE: Record<FloodRisk, string> = {
  minimal: 'text-emerald-300 ring-emerald-400/50',
  moderate: 'text-amber-300 ring-amber-400/50',
  high: 'text-red-300 ring-red-400/60',
  undetermined: 'text-stone-300 ring-stone-500/50',
}

/** The round mark every tile in this column leads with (Crime & safety uses it too). */
export function Badge({ icon: Icon, tone, mark }: { icon: LucideIcon; tone: string; mark?: 'check' | 'missing' }) {
  // Nothing on file is drawn as an empty, dashed outline; something on file as a solid ring.
  const outline = mark === 'missing' ? 'border border-dashed border-stone-600' : `ring-1 ${tone}`
  return (
    <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black/40 ${outline} ${tone.split(' ')[0]}`}>
      <Icon size={17} aria-hidden="true" />
      {mark === 'check' ? (
        <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-400 text-black">
          <Check size={9} strokeWidth={3} aria-hidden="true" />
        </span>
      ) : mark === 'missing' ? (
        <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-stone-600 text-stone-200">
          <Minus size={9} strokeWidth={3} aria-hidden="true" />
        </span>
      ) : null}
    </span>
  )
}

function Tile({ label, state, title, children }: { label: string; state: string; title?: string; children: ReactNode }) {
  return (
    <div
      className="flex min-w-0 flex-col items-center gap-1 rounded-md border border-white/[0.06] bg-black/30 px-1.5 py-2 text-center"
      data-testid={`risk-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
      data-state={state}
      title={title}
    >
      {children}
      <span className="font-mono text-[8px] uppercase tracking-widest text-stone-500">{label}</span>
    </div>
  )
}

export type ReportTile = {
  /** The report's type as the document states it, or the type it was found as. */
  documentType: string
  /** Who prepared it and when, or the file name, when known. */
  note: string | null
}

/** A third-party report: on file (with who and when) or not uploaded. */
export function ReportStatusTile({
  label,
  icon,
  report,
  missingText,
  shortName,
}: {
  label: string
  icon: LucideIcon
  report: ReportTile | null
  /** The whole sentence, for a screen reader and on hover. */
  missingText: string
  /** What the report is called in a word or two ("PCA", "Phase I ESA"). */
  shortName: string
}) {
  if (!report) {
    return (
      <Tile label={label} state="missing" title={missingText}>
        <Badge icon={icon} tone="text-stone-500" mark="missing" />
        <span className="text-[11px] font-medium leading-none text-stone-400" aria-hidden="true">Not uploaded</span>
        <span className="text-[8px] leading-none text-stone-600" aria-hidden="true">{shortName}</span>
        <span className="sr-only">{missingText}</span>
      </Tile>
    )
  }
  const onFile = `${report.documentType} on file`
  return (
    <Tile label={label} state="on-file" title={[onFile, report.note].filter(Boolean).join(' · ')}>
      <Badge icon={icon} tone="text-emerald-300 ring-emerald-400/50" mark="check" />
      <span className="text-[11px] font-medium leading-none text-emerald-200" aria-hidden="true">On file</span>
      <span className="sr-only">{onFile}</span>
      {report.note ? <span className="w-full truncate text-[8px] leading-tight text-stone-500">{report.note}</span> : null}
    </Tile>
  )
}

/** The flood zone, placed on FEMA's three-step scale. */
export function FloodZoneTile({ text, reason }: { text: string | null; reason?: string | null }) {
  const reading = readFloodZone(text)
  if (!reading) {
    return (
      <Tile label="Flood zone" state="none" title={reason ?? 'No FEMA flood zone on record for this deal.'}>
        <Badge icon={Waves} tone="text-stone-500" mark="missing" />
        <span className="text-[11px] font-medium leading-none text-stone-400">Not on record</span>
        <div className="flex w-full max-w-[84px] gap-0.5" aria-hidden="true">
          {SCALE.map((step) => <span key={step.risk} className="h-1 flex-1 rounded-full bg-stone-700/70" />)}
        </div>
      </Tile>
    )
  }
  const caption = RISK_CAPTION[reading.risk]
  return (
    <Tile
      label="Flood zone"
      state={reading.risk}
      // The recorded text after the zone, when it says more than the code itself.
      title={[`Zone ${reading.zone}: ${caption}`, text?.trim().toUpperCase() === reading.zone ? null : text].filter(Boolean).join(' — ')}
    >
      <Badge icon={Waves} tone={RISK_TONE[reading.risk]} />
      <span className={`font-mono text-[13px] font-semibold leading-none ${RISK_TONE[reading.risk].split(' ')[0]}`}>
        {reading.zone}
      </span>
      <div className="flex w-full max-w-[84px] gap-0.5" role="img" aria-label={`Zone ${reading.zone}, ${caption}`}>
        {SCALE.map((step) => (
          <span
            key={step.risk}
            data-lit={step.risk === reading.risk ? 'true' : 'false'}
            className={`h-1 flex-1 rounded-full ${step.risk === reading.risk ? step.lit : 'bg-stone-700/70'}`}
          />
        ))}
      </div>
      <span className="text-[8px] leading-none text-stone-400">{caption}</span>
    </Tile>
  )
}

export const CONDITION_ICON = ClipboardCheck
export const ENVIRONMENTAL_ICON = Leaf
